import React, { memo, useMemo, useState } from 'react';

import { alpha, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { Waypoint } from 'react-waypoint';

import { MenuSectionNavType } from '../../../../../types/menu';
import * as Api from '../../../../../weborder/Api';
import { Typography } from '../../../../atoms';
import { Hidden } from '../../../../layouts';
import { TabsNav } from '../../../../molecules';
import { ItemVariant } from '../../../../molecules/TabsNav/TabsNav';
import { menuSectionUtils } from '../../menuSectionUtils';
import { ConcessionNav } from '../ConcessionNav';
import { ConcessionStoreNavList } from '../ConcessionStoreNavList';

type RouteParams = {
  concessionStoreId?: number;
  id?: number;
  type?: 'collection' | 'delivery';
  vrid: number;
};

interface Props {
  activeMenuSectionId: number;
  availableMenuSections: any[];
  menu: Api.MenuReposnse;
  onTabClick: (sectionId: number) => void;
  params?: RouteParams;
  showSectionLinks: boolean;
  timezone: string;
  translate: any;
  viewportTopOffset: number;
}

const useStyles = (concessionNavHeight, viewportTopOffset) =>
  makeStyles((theme: Theme) => ({
    concessionNavigation: {
      background: theme.palette.common.white,

      '& .fd-tabsnav': {
        borderTop: 'unset',

        '&:first-of-type': {
          marginBottom: theme.spacing(2),
        },
      },
    },

    concessionNavTitle: {
      padding: theme.spacing(1.5, 2),
    },

    navigation: {
      position: 'sticky',
      top: `${viewportTopOffset}px`,
      zIndex: 1,

      '& .mobileNav': {
        background: theme.palette.common.white,
        borderBottom: `1px solid ${theme.palette.grey[400]}`,
        maxWidth: '100vw',
        padding: theme.spacing(0, 2),
        zIndex: 1,

        '&:after': {
          background: `linear-gradient(90deg, ${theme.palette.common.white} 0%, ${theme.palette.common.white} 25%, ${theme.palette.common.white} 100%)`,
          content: '""',
          height: '48px',
          pointerEvents: 'none',
          position: 'absolute',
          right: 0,
          top: 0,
          width: '26px',
        },
        '& .MuiTab-root': {
          padding: theme.spacing(1.5, 0),
          margin: 0,
          marginRight: theme.spacing(2),
        },
        '& .MuiTabs-scroller': {
          marginRight: '20px',
          width: '4000px',
        },
        '& .Mui-selected': {
          boxShadow: `inset 0px -3px 0px 0px ${theme.palette.primary.main}`,
        },
        '& .MuiTab-wrapper': {
          display: 'inline-block',
          maxWidth: '30ch',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
    },

    sections: {
      position: 'relative',
      zIndex: 0,
    },

    stickyDesktopNavBarFullWidthBar: {
      background: alpha(theme.palette.common.white, 0.95),
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
      height: `${concessionNavHeight}px`,
      left: 0,
      position: 'fixed',
      top: `${viewportTopOffset}px`,
      width: '100%',
      zIndex: theme.zIndex.appBar,
    },

    stickyDesktopNavBar: {
      position: 'fixed',
      top: `${viewportTopOffset}px`,
      width: theme.spacing(100),
      zIndex: theme.zIndex.appBar,

      '& .fd-tabsnav': {
        borderTop: 'none',
      },

      '& $concessionNavigation': {
        '& .fd-tabsnav': {
          '&:first-of-type': {
            margin: theme.spacing(1.75, 0, 0),
          },
        },
      },
    },

    [theme.breakpoints.up('sm')]: {
      concessionNavigation: {
        '& .fd-tabsnav': {
          '&:first-of-type': {
            margin: theme.spacing(0, 3, 2),
          },
        },
      },

      concessionNavTitle: {
        padding: theme.spacing(1.5, 3),
      },

      navigation: {
        '& .mobileNav': {
          padding: theme.spacing(0, 3),
        },
      },
    },

    [theme.breakpoints.up('md')]: {
      concessionNavigation: {
        backgroundColor: 'unset',
        '& .fd-tabsnav': {
          '&:first-of-type': {
            margin: theme.spacing(0, 0, 1.75),
          },
        },
      },

      concessionNavTitle: {
        padding: theme.spacing(1.5, 0),
      },

      navigation: {
        position: 'initial',
        top: `${viewportTopOffset}px`,
        zIndex: 1,

        '& .desktopNav': {
          display: 'inherit',
          maxWidth: theme.spacing(100),
          padding: theme.spacing(2, 0),
          zIndex: 1,

          '& .MuiTabs-scroller': {
            width: theme.spacing(100),
          },
        },
      },
    },
  }))();

const DESKTOP_CONCESSION_NO_SECTION_NAV_HEIGHT = 86;
const DESKTOP_CONCESSION_STICKY_NAV_HEIGHT = 132;
const DESKTOP_STICKY_NAV_HEIGHT = 65;
const EXTRA_CONCESSION_OFFSET_TOP_FOR_WAYPOINT = 14;

function MenuNavigationComponent(props: Props): JSX.Element {
  const {
    activeMenuSectionId,
    availableMenuSections,
    menu,
    onTabClick,
    params,
    showSectionLinks,
    timezone,
    translate,
    viewportTopOffset,
  } = props;

  const concessionStoreList = useMemo(
    () => menuSectionUtils.getConcessionStoresAndAvailability(menu, timezone),
    [menu.MenuId, menu.ConcessionStores, timezone],
  );
  const hasConcessionStores = concessionStoreList.length > 0;

  const concessionHeightsVariants = showSectionLinks
    ? DESKTOP_CONCESSION_STICKY_NAV_HEIGHT
    : DESKTOP_CONCESSION_NO_SECTION_NAV_HEIGHT;

  const concessionNavHeight = hasConcessionStores
    ? concessionHeightsVariants
    : DESKTOP_STICKY_NAV_HEIGHT;

  const classes = useStyles(concessionNavHeight, viewportTopOffset);
  const [showStickyDesktopNavbar, setShowStickyDesktopNavbar] = useState(false);

  const getNavTitle = (name: string): string => {
    if (!name) return null;

    if (name !== '' && name !== 'Untitled Section') {
      return name.trim();
    }
    return translate('Untitled_section');
  };

  const getSectionNavConfig = (menuSections) =>
    menuSections.map(
      (menuSection) =>
        ({
          id: Number(menuSection.MenuSectionId),
          label: getNavTitle(menuSection.Name),
        } as MenuSectionNavType),
    );

  const availableSections = getSectionNavConfig(availableMenuSections);

  const activeConcessionStoreId = Number(params?.concessionStoreId);
  const isMenuDisplayReady =
    (hasConcessionStores && activeConcessionStoreId) || !hasConcessionStores;

  const concessionNavStyles = clsx(
    hasConcessionStores && classes.concessionNavigation,
  );

  const renderMobileMenuNav = (): JSX.Element => {
    const mobileSectionNavStyles = clsx(
      'mobileNav',
      'fd-section__menu-nav-mobile',
    );

    return (
      <div className={concessionNavStyles}>
        {hasConcessionStores && (
          <ConcessionNav
            activeConcessionStoreId={activeConcessionStoreId}
            concessionStoreList={concessionStoreList}
            params={params}
            translate={translate}
          />
        )}

        {showSectionLinks && (
          <TabsNav
            className={mobileSectionNavStyles}
            itemVariant={ItemVariant.Tab}
            onTabClick={onTabClick}
            TabIndicatorProps={{ style: { display: 'none' } }}
            tabs={availableSections}
            value={activeMenuSectionId}
          />
        )}
      </div>
    );
  };

  const renderDesktopInlineSectionNavBar = (): JSX.Element => {
    if (!isMenuDisplayReady) {
      return null;
    }

    const desktopSectionNavStyles = clsx(
      'desktopNav',
      'fd-section__menu-nav-desktop',
    );

    return (
      <div className={concessionNavStyles}>
        {hasConcessionStores && (
          <ConcessionNav
            activeConcessionStoreId={activeConcessionStoreId}
            concessionStoreList={concessionStoreList}
            params={params}
            translate={translate}
          />
        )}

        {showSectionLinks && (
          <TabsNav
            className={desktopSectionNavStyles}
            itemVariant={ItemVariant.Chip}
            onTabClick={onTabClick}
            TabIndicatorProps={{ style: { display: 'none' } }}
            tabs={availableSections}
            value={activeMenuSectionId}
          />
        )}
      </div>
    );
  };

  const inlineNavBar = renderDesktopInlineSectionNavBar();

  const renderDesktopMenuNav = (): JSX.Element => {
    let stickyNavBar = null;

    const offsetTop = hasConcessionStores
      ? viewportTopOffset + EXTRA_CONCESSION_OFFSET_TOP_FOR_WAYPOINT
      : viewportTopOffset;

    if (showStickyDesktopNavbar && (showSectionLinks || hasConcessionStores)) {
      stickyNavBar = (
        <>
          <div className={classes.stickyDesktopNavBarFullWidthBar} />
          <div className={classes.stickyDesktopNavBar}>{inlineNavBar}</div>
        </>
      );
    }

    return (
      <>
        {stickyNavBar}

        <Waypoint
          fireOnRapidScroll
          onEnter={(): void => setShowStickyDesktopNavbar(false)}
          onLeave={(event): void => {
            const isWaypointBelowViewport = event.currentPosition === 'below';
            if (!isWaypointBelowViewport) {
              setShowStickyDesktopNavbar(true);
            }
          }}
          topOffset={offsetTop}
        />

        {inlineNavBar}
      </>
    );
  };

  const menuSectionNavigation = useMemo(() => {
    if (!availableMenuSections || !availableMenuSections.length) {
      return null;
    }

    return (
      <nav className="fd-section__menu-nav">
        <Hidden mdDown>{renderDesktopMenuNav()}</Hidden>
        <Hidden lgUp>{renderMobileMenuNav()}</Hidden>
      </nav>
    );
  }, [
    activeMenuSectionId,
    availableMenuSections,
    inlineNavBar,
    showStickyDesktopNavbar,
  ]);

  const navigationStyles = clsx(classes.navigation, 'fd-section__menu-nav');

  return (
    <>
      {hasConcessionStores && (
        <Typography className={classes.concessionNavTitle}>
          <em>{translate('Menu_menus_title')}</em>
        </Typography>
      )}

      <div className={navigationStyles} data-fd="menu-navigation">
        {hasConcessionStores && !activeConcessionStoreId && (
          <ConcessionStoreNavList
            concessionStoreList={concessionStoreList}
            params={params}
            translate={translate}
          />
        )}

        {isMenuDisplayReady && menuSectionNavigation}
      </div>
    </>
  );
}

const MenuNavigation = memo(MenuNavigationComponent);
MenuNavigation.displayName = 'MenuNavigation';

export { MenuNavigation };
